/**
 * Document Request Service
 * 요청 서류 API 서비스
 */

import api from "./api";
import type {
  AssigneeCandidatesResponse,
  DocumentRequest,
  DocumentRequestAnswers,
  DocumentRequestFile,
  DocumentRequestListResponse,
  DocumentRequestCreateRequest,
  DocumentRequestUpdateRequest,
  DocumentRequestListParams,
  UnsubmittedSummaryResponse,
} from "@/types/documentRequest";

/** Content-Disposition(filename*=UTF-8'') 에서 파일명 추출, 없으면 fallback */
const resolveFileName = (contentDisposition: string | undefined, fallback: string) => {
  if (contentDisposition) {
    const match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
    if (match && match[1]) return decodeURIComponent(match[1]);
  }
  return fallback;
};

const triggerBlobDownload = (data: BlobPart, fileName: string) => {
  const blob = new Blob([data]);
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

const documentRequestService = {
  /**
   * 내가 받은 요청 중 대기 건수 조회
   */
  async getReceivedPendingCount(): Promise<{ count: number }> {
    const response = await api.get<{ count: number }>("/document-requests/my/pending/count");
    return response.data;
  },

  /**
   * 관리자 - 미제출 담당자가 있는 서류 요청 요약 (대시보드용)
   * 권한이 없으면 has_access=false 반환
   */
  async getUnsubmittedSummary(): Promise<UnsubmittedSummaryResponse> {
    const response = await api.get<UnsubmittedSummaryResponse>(
      "/document-requests/admin/unsubmitted-summary"
    );
    return response.data;
  },

  /**
   * 관리자 - 서류 요청 목록 조회
   */
  async getList(params?: DocumentRequestListParams): Promise<DocumentRequestListResponse> {
    const response = await api.get<DocumentRequest[]>("/document-requests/admin", {
      params,
    });

    // Backend returns array, not paginated response
    // Adapt to expected frontend format
    const items = response.data;
    const page = params?.page || 1;
    const pageSize = params?.page_size || 10;

    return {
      items,
      total: items.length, // Note: backend doesn't provide total count
      page,
      page_size: pageSize,
      total_pages: Math.ceil(items.length / pageSize),
    };
  },

  /**
   * 직원 - 내가 받은 서류 요청 목록 조회
   */
  async getMyRequests(page: number = 1, pageSize: number = 10): Promise<any> {
    const response = await api.get<any[]>("/document-requests/my", {
      params: { page, page_size: pageSize },
    });

    // Backend returns array, not paginated response
    // Adapt to expected frontend format
    const items = response.data;

    return {
      items,
      total: items.length, // Note: backend doesn't provide total count
      page,
      page_size: pageSize,
      total_pages: Math.ceil(items.length / pageSize),
    };
  },

  /**
   * 관리자 - 상세 조회
   */
  async getDetail(id: number): Promise<DocumentRequest> {
    const response = await api.get<DocumentRequest>(`/document-requests/admin/${id}`);
    return response.data;
  },

  /**
   * 직원 - 내 서류 요청 상세 조회
   */
  async getMyDetail(assigneeId: number): Promise<DocumentRequest> {
    const response = await api.get<DocumentRequest>(`/document-requests/my/${assigneeId}`);
    return response.data;
  },

  /**
   * 생성 (관리자만)
   */
  async create(data: DocumentRequestCreateRequest): Promise<DocumentRequest> {
    const response = await api.post<DocumentRequest>("/document-requests/admin", data);
    return response.data;
  },

  /**
   * 삭제 (관리자만)
   */
  async delete(id: number): Promise<void> {
    await api.delete(`/document-requests/admin/${id}`);
  },

  /**
   * 파일 업로드 (직원)
   */
  async uploadFiles(assigneeId: number, files: File[]): Promise<DocumentRequestFile[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    const response = await api.post<DocumentRequestFile[]>(
      `/document-requests/my/${assigneeId}/files`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  /**
   * 파일 다운로드
   */
  async downloadFile(fileId: number, fileName: string): Promise<void> {
    const response = await api.get(`/document-requests/files/${fileId}/download/stream`, {
      responseType: "blob",
    });

    // blob URL 생성 및 다운로드 트리거
    const blob = new Blob([response.data]);
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  },

  /**
   * 제출 (직원)
   */
  async submitRequest(assigneeId: number): Promise<void> {
    await api.post(`/document-requests/my/${assigneeId}/submit`);
  },

  /**
   * 답변 저장/제출 (직원, survey 유형)
   * submit=true 면 저장과 동시에 제출 (필수 질문 미응답 시 400)
   */
  async saveAnswers(
    assigneeId: number,
    answers: DocumentRequestAnswers,
    submit: boolean
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/document-requests/my/${assigneeId}/answers`, {
      answers,
      submit,
    });
    return response.data;
  },

  /**
   * 답변 응답 Excel 다운로드 (관리자, survey 유형)
   */
  async exportAnswers(requestId: number, fallbackName: string): Promise<void> {
    const response = await api.get(`/document-requests/admin/${requestId}/answers/export`, {
      responseType: "blob",
    });
    triggerBlobDownload(
      response.data,
      resolveFileName(response.headers["content-disposition"], `${fallbackName}_응답.xlsx`)
    );
  },

  /**
   * 제출 취소 (직원)
   */
  async cancelSubmission(assigneeId: number): Promise<void> {
    await api.post(`/document-requests/my/${assigneeId}/cancel-submit`);
  },

  /**
   * 파일 삭제
   */
  async deleteFile(fileId: number): Promise<void> {
    await api.delete(`/document-requests/files/${fileId}`);
  },

  /**
   * 승인 (관리자)
   */
  async approve(assigneeId: number): Promise<void> {
    await api.post(`/document-requests/admin/assignees/${assigneeId}/approve`);
  },

  /**
   * 반려 (관리자)
   */
  async reject(assigneeId: number, reason: string): Promise<void> {
    await api.post(`/document-requests/admin/assignees/${assigneeId}/reject`, { reason });
  },

  /**
   * 댓글 작성
   */
  async createComment(requestId: number, content: string): Promise<any> {
    const response = await api.post("/document-requests/comments", {
      request_id: requestId,
      content,
    });
    return response.data;
  },

  /**
   * 미제출 담당자 재촉 - 푸시 + 이메일 (관리자)
   */
  async nudge(requestId: number): Promise<{
    success: boolean;
    message: string;
    target_count: number;
    cooldown_seconds: number;
  }> {
    const response = await api.post(`/document-requests/admin/${requestId}/nudge`);
    return response.data;
  },

  /**
   * 서류 요청 수정 (관리자)
   */
  async updateRequest(
    requestId: number,
    data: DocumentRequestUpdateRequest
  ): Promise<void> {
    await api.patch(`/document-requests/admin/${requestId}`, data);
  },

  /**
   * 아직 대상이 아닌 재직자 목록 (관리자) — 중간 입사자는 joined_after_request=true 로 맨 위
   */
  async getCandidates(requestId: number): Promise<AssigneeCandidatesResponse> {
    const response = await api.get<AssigneeCandidatesResponse>(
      `/document-requests/admin/${requestId}/candidates`
    );
    return response.data;
  },

  /**
   * 담당자 추가 (관리자) — 추가된 담당자에게 요청 알림 발송
   */
  async addAssignees(
    requestId: number,
    assigneeIds: string[]
  ): Promise<{ success: boolean; message: string; added_count: number; skipped_existing: number }> {
    const response = await api.post(`/document-requests/admin/${requestId}/assignees`, {
      assignee_ids: assigneeIds,
    });
    return response.data;
  },

  /**
   * 담당자 제거 (관리자)
   */
  async removeAssignee(requestId: number, assigneeId: number): Promise<void> {
    await api.delete(`/document-requests/admin/${requestId}/assignees/${assigneeId}`);
  },

  /**
   * 템플릿 파일 업로드 (관리자)
   */
  async uploadTemplateFiles(requestId: number, files: File[]): Promise<DocumentRequest> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    const response = await api.post<DocumentRequest>(
      `/document-requests/admin/${requestId}/template-files`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  /**
   * 템플릿 파일 다운로드
   */
  async downloadTemplateFile(fileId: number, fileName: string): Promise<void> {
    const response = await api.get(`/document-requests/template-files/${fileId}/download`, {
      responseType: "blob",
    });

    // blob URL 생성 및 다운로드 트리거
    const blob = new Blob([response.data]);
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  },

  /**
   * 템플릿 파일 삭제 (관리자)
   */
  async deleteTemplateFile(fileId: number): Promise<void> {
    await api.delete(`/document-requests/admin/template-files/${fileId}`);
  },

  /**
   * 일괄 파일 다운로드 (관리자)
   */
  async downloadAllFiles(requestId: number, baseFilename: string): Promise<void> {
    const response = await api.post(
      `/document-requests/admin/${requestId}/download-all?base_filename=${encodeURIComponent(baseFilename)}`,
      null,
      {
        responseType: "blob",
      }
    );

    // blob URL 생성 및 다운로드 트리거
    const blob = new Blob([response.data]);
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;

    // Content-Disposition 헤더에서 파일명 추출
    const contentDisposition = response.headers["content-disposition"];
    let fileName = `${baseFilename}.zip`;
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = decodeURIComponent(fileNameMatch[1]);
      }
    }

    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  },

  // ========== Legacy methods (to be removed) ==========
  async update(_id: number, _data: any): Promise<any> {
    throw new Error("Not implemented - use new API");
  },
  async submit(_id: number): Promise<any> {
    throw new Error("Not implemented - use new API");
  },
  async complete(_id: number): Promise<any> {
    throw new Error("Not implemented - use new API");
  },
  async cancel(_id: number): Promise<any> {
    throw new Error("Not implemented - use new API");
  },
  async deleteFileLegacy(_requestId: number, _fileId: number): Promise<void> {
    throw new Error("Not implemented - use new API");
  },
};

export default documentRequestService;
